<template>
  <div v-if="isImpersonating" class="impersonation-banner" role="alert" aria-live="assertive">
    <div class="impersonation-banner__inner">
      <div class="impersonation-banner__badge">
        <Icon icon="mdi:shield-account" width="14" height="14" aria-hidden="true" />
        ADMIN VIEW
      </div>
      <span class="impersonation-banner__text">
        Viewing account of
        <strong>{{ info.userName || info.userEmail || "Unknown User" }}</strong>
        <span v-if="info.userName && info.userEmail" class="impersonation-banner__email">
          · {{ info.userEmail }}
        </span>
      </span>
      <button class="impersonation-banner__exit" @click="handleExit">
        <Icon icon="mdi:logout-variant" width="13" height="13" aria-hidden="true" />
        Exit Session
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Icon } from "@/components/ui";
import { LOGIN_ROUTE } from "@/router/routes";
import { CLEAR_IMPERSONATION, SET_SESSION_TOKEN, SET_ACCOUNT } from "@/store/mutation-types";
import { LOCAL_STORAGE_KEY, IMPERSONATION_SESSION_KEY } from "@/utils/const";

const store = useStore();
const router = useRouter();

const isImpersonating = computed(() => store.getters.isImpersonating);
const info = computed(() => store.getters.impersonationInfo);

const handleExit = () => {
  store.commit(CLEAR_IMPERSONATION);
  store.commit(SET_SESSION_TOKEN, null);
  store.commit(SET_ACCOUNT, { data: null, isLoading: false });
  sessionStorage.removeItem(LOCAL_STORAGE_KEY);
  sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
  try {
    window.close();
  } catch {}
  router.push({ name: LOGIN_ROUTE });
};
</script>

<style scoped>
.impersonation-banner {
  width: 100%;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  color: #7f1d1d;
  font-size: 0.75rem;
  line-height: 1;
  flex-shrink: 0;
}

.impersonation-banner__inner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
}

.impersonation-banner__badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 4px;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #b91c1c;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  flex-shrink: 0;
}

.impersonation-banner__text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #991b1b;
}

.impersonation-banner__email {
  opacity: 0.7;
  font-weight: 400;
}

.impersonation-banner__exit {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 3px 9px;
  border-radius: 4px;
  border: 1px solid #fca5a5;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;
}

.impersonation-banner__exit:hover {
  background: #fecaca;
  border-color: #f87171;
}
</style>
